import { useState, useEffect, useContext } from "react";
import { v4 as uuid4 } from 'uuid';
import {
  getProvidersRaw,
  getGroups,
} from "modules/agentMySecurity/services/embeddingsService";
import { Group, Iau } from "../../admin.type";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  IconButton,
} from "@mui/material";
import {
  SimpleTableContainer,
} from "../../admin.styled";
import AddIcon from "@mui/icons-material/Add";
import AdvanceSearch from "core/components/global/advanceSearch/advanceSearch";
import NoResultsFound from "core/components/global/tables/noResults/noResultsFound";
import { AdminIauRow } from "./iau";
import { AuthContext } from "core/contexts/auth/auth";


// Define sort types
type SortColumn = any;
type SortDirection = 'asc' | 'desc' | null;

export default function AdminIaus() {
    const { checkAMSToken } = useContext(AuthContext);
    const [iaus, setIaus] = useState<Iau[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [filteredIaus, setFilteredIaus] = useState<Iau[] | null>(null);
    const [copyOfSearchValue, setCopyOfSearchValue] = useState("");

    // Column sorting state
    const [sortColumn, setSortColumn] = useState<SortColumn>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);

    useEffect(() => {
        async function fetchIaus() {
            try {
                const amsBearerToken = await checkAMSToken();
                const response = await getProvidersRaw(amsBearerToken);
                setIaus(response);
            } catch (error) {
                setIaus([]);
            }
        }
        
        async function fetchGroups() {
            try {
                const response = await getGroups();
                setGroups(response);
            } catch (error) {
                setGroups([]);
            }
        }
        
        fetchIaus();
        fetchGroups();
    }, []);

    /**
     * Apply sorting whenever sort parameters change
     */
    useEffect(() => {
        if (!sortColumn || !sortDirection) {
            return;
        }

        const dataToSort = [...(filteredIaus === null ? iaus : filteredIaus)];

        const sortedData: any = dataToSort.sort((a, b) => {
            const valA = a.options[sortColumn] ? String(a.options[sortColumn]).toLowerCase() : a[sortColumn] ? String(a[sortColumn]).toLowerCase() : "";
            const valB = b.options[sortColumn] ? String(b.options[sortColumn]).toLowerCase() : b[sortColumn] ? String(b[sortColumn]).toLowerCase() : "";

            if (valA < valB) {
                return sortDirection === 'asc' ? -1 : 1;
            }

            if (valA > valB) {
                return sortDirection === 'asc' ? 1 : -1;
            }

            return 0;
        });

        setFilteredIaus(filteredIaus === null ? sortedData : sortedData);
    }, [sortColumn, sortDirection]);

    /**
     * Handle column header click to sort
     */
    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (column: SortColumn) => {
        if (sortColumn !== column) {
            return null;
        }
        return sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" className="ms-1" />
            : <ArrowDownwardIcon fontSize="small" className="ms-1" />;
    };

    const handleFilterData = (newKeys, b, c) => {
        setFilteredIaus(newKeys);
        setSortColumn(null);
        setSortDirection(null);
    };

    const handleClearFilter = () => {
        setFilteredIaus(null);
    };

    return (
        <SimpleTableContainer elevation={1} columns={3}>
            {/* Data Search */}
            <div className="pb-3 d-flex justify-content-between align-items-center">
                <h5>IAUs</h5>
                <AdvanceSearch
                    initialData={iaus}
                    setFilteredData={handleFilterData}
                    clearFilteredData={handleClearFilter}
                    setCopyOfSearchValue={setCopyOfSearchValue}
                />
            </div>
            {/* Collapsable Table */}
            <div aria-label="collapsible table">
                <div style={{ borderRadius: "5px 5px 0 0" }} className="outer-header-container">
                    <div className="row-items row-headers">
                        <p className="header-item d-flex align-items-center" onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>
                            Description {getSortIcon('description')}
                        </p>
                        <p className="header-item d-flex align-items-center" onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                            Name {getSortIcon('name')}
                        </p>
                        <p className="header-item d-flex align-items-center" onClick={() => handleSort('org')} style={{ cursor: 'pointer' }}>
                            Organization {getSortIcon('org')}
                        </p>
                    </div>
                    <div className="row-buttons">
                        <IconButton aria-label="send prompt" onClick={() => console.log("Add new IAU")} edge="end" className="me-1 icon-button">
                            <AddIcon />
                        </IconButton>
                    </div>
                </div>

                <div
                    style={{ borderRadius: "0 0 5px 5px", minHeight: "406px" }}
                    className="outer-row-container"
                    >
                    {(filteredIaus ?? iaus).length > 0 ? (
                        (filteredIaus ?? iaus).map((iau) => (
                        <AdminIauRow key={uuid4()} iau={iau} />
                        ))
                    ) : (
                        <NoResultsFound searchTerm={filteredIaus !== null ? copyOfSearchValue : ""} />
                    )}
                </div>
            </div>
        </SimpleTableContainer>
    );
}